import { playableLength, ROOT_ID, type Rect, type Scene, type SceneChange } from '@flobi/artwork-core';
import type { Camera } from '@flobi/artwork-core/render';

/**
 * Which artboards move, where the mark that says so goes, and what a press on it means.
 *
 * An artboard that plays looks exactly like one that does not until somebody opens Animate
 * on it, so a page of twenty boards gave no way to tell which two were the videos. The mark
 * is a small play button in a box outside the board's top right corner — outside, because
 * inside it would be read as part of the design and would sit on the artwork it is about —
 * and pressing it plays the page.
 */

/**
 * The mark, in screen pixels: its side, how far above the board it stands, and how far
 * round it a press still counts. The glyph is Streamline Nova Line's "Play Button Square"
 * (ico_OC1vWZaZi4ORJKS2), the family the rest of the controls wear.
 */
export const MARK = { size: 12, gap: 6, reach: 4 } as const;
/** Nova Line "Play Button Square", on its own 24-unit grid; filled even-odd. */
export const MARK_GLYPH =
  'M1 2c0 -0.55228 0.44772 -1 1 -1h20c0.5523 0 1 0.44772 1 1v20c0 0.5523 -0.4477 1 -1 1H2c-0.55228 0 -1 -0.4477 -1 -1zm2 1v18h18V3zm9 4c-2.76142 0 -5 2.23858 -5 5 0 2.7614 2.23858 5 5 5 2.7614 0 5 -2.2386 5 -5 0 -2.76142 -2.2386 -5 -5 -5m-7 5c0 -3.86599 3.13401 -7 7 -7 3.866 0 7 3.13401 7 7 0 3.866 -3.134 7 -7 7 -3.86599 0 -7 -3.134 -7 -7m5.5 -3 4 3 -4 3z';
/** A board narrower than this on screen gets no mark: zoomed that far out it would be wider than what it marks. */
const LEAST_BOARD = 48;

/**
 * The artboards with something that moves, comes or goes, in the order they sit in the file.
 *
 * Asked of the engine rather than read off the board's own `animation`: a board that says
 * nothing but holds one layer with a move of its own still plays, and is what Animate
 * would play. A hidden board is left out — there is no corner on screen to mark.
 */
export function movingBoards(scene: Scene): string[] {
  return scene.childrenOf(ROOT_ID).filter((id) => {
    const node = scene.get(id);
    return node?.type === 'artboard' && node.visible !== false && playableLength(scene, id) !== null;
  });
}

/** What a layer has to change for a board to start or stop moving. */
const TELLING = ['*', 'animation', 'timing', 'visible'];

/**
 * Whether a change to the page could change the answer above.
 *
 * Dragging a shape writes its transform sixty times a second, and none of those writes can
 * make a still board move; asking the engine again after each would read every layer of
 * every board on every frame of every drag. Only layers coming, going or changing parent,
 * and the few properties playing is decided by, are worth asking again for.
 */
export function mayChangeMoving(change: Pick<SceneChange, 'added' | 'removed' | 'moved' | 'updated'>): boolean {
  if (change.added.size > 0 || change.removed.size > 0 || change.moved.size > 0) return true;
  for (const update of change.updated.values()) if (TELLING.some((key) => update.keys.has(key))) return true;
  return false;
}

/**
 * Where a board's mark goes, in screen pixels: its top left corner. Flush with the board's
 * right edge and a little above its top one, the same size at any zoom. Null when the board
 * is too small on screen to carry one.
 */
export function markPlace(board: Rect, camera: Camera): { x: number; y: number } | null {
  if (board.width * camera.zoom < LEAST_BOARD) return null;
  return {
    x: (board.x + board.width - camera.x) * camera.zoom - MARK.size,
    y: (board.y - camera.y) * camera.zoom - MARK.gap - MARK.size,
  };
}

/**
 * The board whose mark is under a point on the screen, if any. Asked of the boards from the
 * front, so where two marks overlap the one drawn on top takes the press. A little wider
 * than the mark itself: twelve pixels is a small thing to aim at.
 */
export function markUnder(boards: readonly { id: string; box: Rect }[], camera: Camera, at: { x: number; y: number }): string | null {
  for (let index = boards.length - 1; index >= 0; index--) {
    const { id, box } = boards[index]!;
    const place = markPlace(box, camera);
    if (!place) continue;
    const inside = (value: number, from: number) => value >= from - MARK.reach && value <= from + MARK.size + MARK.reach;
    if (inside(at.x, place.x) && inside(at.y, place.y)) return id;
  }
  return null;
}
